import {
  JupiterTheme,
  UserAvatar,
  MockHook,
} from '@jupiterone/jupiter-web-toolkit';
import Divider from '@material-ui/core/Divider';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';
import { createStyles, makeStyles } from '@material-ui/styles';
import clsx from 'clsx';
import React from 'react';
import {
  Check as ActiveAccountIcon,
  LogOut as ExitIcon,
  Plus as AddIcon,
} from 'react-feather';
import Fade from '@material-ui/core/Fade';
import AddAccountModal from 'navbar/src/jupiterAppBar/AddAccount/AddAccountModal';
import useAccountMenuLogic, {
  AccountMenuComponentProps,
} from './useAccountMenuLogic';

export const SUPPORT_USER_EMAIL = 'callisto@jupiterone.io';
const ACCT_MENU_ID = 'acct-menu';

const useStyles = makeStyles((theme: JupiterTheme) => {
  const { palette, spacing } = theme;
  const CONTENT_SIZE = '2rem';
  const NAV_ICON_SIZE_SPACING = spacing(2.5);

  const titleText = (spacing: any) => ({
    marginLeft: spacing,
    lineHeight: CONTENT_SIZE,
    color: palette.text.dark,
  });

  return createStyles({
    name: {
      marginLeft: 8,
    },
    userEmpty: {
      ...titleText(NAV_ICON_SIZE_SPACING),
      marginLeft: 0,
      paddingLeft: 0,
    },
    navAvatar: {
      display: 'flex',
      alignItems: 'center',
      marginLeft: NAV_ICON_SIZE_SPACING,
      cursor: 'pointer',
      '&:focus': {
        outline: 'none',
        color: palette.primary.main,
      },
    },
    menuPaper: {
      borderTop: `solid 1px ${palette.grey[500]}`,
      border: 'none',
      borderRadius: 0,
      display: 'flex',
      flexDirection: 'column',
    },
    accountMenu: {
      left: 'auto !important',
      right: 10,
    },
    listMenu: {
      marginTop: theme.spacing(5.5),
      marginRight: 0,
      marginLeft: -theme.spacing(3),
      display: 'flex',
      flexDirection: 'column',
    },
    popCollapsed: {
      '& $menuPaper': {
        borderTop: 'none',
        marginTop: 64,
        right: 10,
      },
    },
    menuSubheading: {
      color: palette.coolGrey[600],
      fontSize: '12px',
      letterSpacing: '1.6px',
      outline: 'none',
      padding: `${theme.spacing(1.25)}px ${theme.spacing(
        3.25
      )}px ${theme.spacing(2)}px`,
    },
    menuHeader: {
      alignItems: 'center',
      display: 'flex',
      padding: `${theme.spacing(1)}px ${theme.spacing(2.5)}px`,
    },
    menuItem: {
      paddingRight: theme.spacing(3.25),
      paddingLeft: theme.spacing(3.25),
      justifyContent: 'flex-start',
      '& a': {
        textDecoration: 'none',
        display: 'flex',
        color: 'inherit',
        '&:visited': {
          color: 'inherit',
        },
      },
      '& b': {
        color: palette.coolGrey[400],
        transition: 'color 0.3s',
      },
      '&:hover': {
        backgroundColor: palette.coolGrey[50],
        '& b': {
          color: palette.blue[700],
        },
      },
    },
    activeMenuItem: {
      background: palette.coolGrey[100],
      color: palette.navy[900],
      cursor: 'default',
      opacity: 1,
      paddingRight: theme.spacing(6.25),
    },
    inactiveMenuItem: {
      paddingLeft: theme.spacing(6.25),
      paddingRight: theme.spacing(6.25),
    },
    menuIcon: {
      display: 'flex',
      color: palette.primary.main,
      marginRight: theme.spacing(1.25),
      transition: 'color 0.3s',
    },
    menuIconSmall: {
      width: 18,
      height: 18,
      marginRight: theme.spacing(0.75),
      strokeWidth: 3,
    },
    menuDivider: {
      margin: `${theme.spacing(1)}px 0`,
    },
    centerFlexContainer: {
      display: 'flex',
      alignItems: 'center',
    },
    addAccount: {
      borderLeft: `1px solid ${theme.palette.grey[500]}`,
      padding: theme.spacing(0.5),
      cursor: 'pointer',
    },
    avatarContainer: {
      alignItems: 'center',
      display: 'flex',
      marginRight: theme.spacing(3),
    },
    nameContainer: {
      display: 'flex',
      flexDirection: 'column',
      marginLeft: NAV_ICON_SIZE_SPACING * 0.6,
    },
    accountName: {
      ...theme.typography.body1,
      display: 'block',
      fontWeight: 'normal',
      lineHeight: '1',
    },
    displayName: {
      ...theme.typography.caption,
      display: 'inline-block',
      fontSize: '.65rem', // approx. 11px but allows font to scale
      lineHeight: '1.2',
      maxWidth: '15em', // Allow displaying only ~36 chars (assuming .5em per char per https://developer.mozilla.org/en-US/docs/Web/CSS/length#units)
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
  });
});

export const AccountMenuComponent = ({
  accountName,
  anchorEl,
  displayName,
  accounts,
  account,
  user,
  addAccountModalOpen,
  collapsed,
  setAnchorEl,
  clearAnchorEl,
  useAccount,
  logout,
}: AccountMenuComponentProps) => {
  const classes = useStyles();
  return (
    <>
      <div className={classes.avatarContainer} onClick={setAnchorEl}>
        <Tooltip title="Account" enterDelay={750}>
          <div className={classes.navAvatar}>
            <UserAvatar name={displayName} size="28px" />
          </div>
        </Tooltip>
        {accountName && (
          <div className={classes.nameContainer}>
            <span className={classes.accountName}>{accountName}</span>
            <span className={classes.displayName}>{displayName}</span>
          </div>
        )}
      </div>
      <Menu
        id={ACCT_MENU_ID}
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        PopoverClasses={{
          paper: clsx(classes.menuPaper, classes.accountMenu),
        }}
        className={clsx(collapsed ? classes.popCollapsed : classes.listMenu)}
        onClose={clearAnchorEl}
        TransitionComponent={Fade}
      >
        {accounts.length > 1 && [
          <div className={classes.menuHeader} key={0}>
            <UserAvatar name={displayName} />
            <Typography className={classes.name} variant="body1">
              {displayName}
            </Typography>
          </div>,
          <Divider className={classes.menuDivider} key={1} />,
          <div className={classes.centerFlexContainer} key={2}>
            <Typography variant="subtitle1" className={classes.menuSubheading}>
              Switch Account
            </Typography>
            {user?.profile.email === SUPPORT_USER_EMAIL && (
              <div className={classes.addAccount}>
                <AddIcon onClick={addAccountModalOpen.setTrue} />
              </div>
            )}
          </div>,
          <MenuItem
            key={3}
            className={clsx(classes.menuItem, classes.activeMenuItem)}
            disabled={true}
          >
            <ActiveAccountIcon
              className={clsx(classes.menuIcon, classes.menuIconSmall)}
            />
            {account?.name}
          </MenuItem>,
          accounts
            .filter((acct) => acct.id !== account?.id)
            .map((acct) => (
              <MenuItem
                key={acct.id}
                onClick={() => useAccount(acct.subdomain)}
                className={clsx(classes.menuItem, classes.inactiveMenuItem)}
              >
                {acct.name}
              </MenuItem>
            )),
          <Divider className={classes.menuDivider} key={5} />,
        ]}
        <MenuItem onClick={logout} className={classes.menuItem}>
          <ExitIcon className={classes.menuIcon} />
          Log out
        </MenuItem>
      </Menu>

      <AddAccountModal
        open={addAccountModalOpen.state}
        onClose={addAccountModalOpen.setFalse}
      />
    </>
  );
};
export default MockHook.withHookHoc(AccountMenuComponent, useAccountMenuLogic);
